import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import { useForm } from 'react-hook-form'
import { defaultFulfillmentCenterTypeFilters } from 'src/features/fulfillmentCenterFilters'
import { selectFulfillmentCenters } from 'src/features/selector'
import { actions as appActions } from '../../../state'
import { FiltersSearchLocationsEnum, GetLocationsPayload, isYesNoOptionsList } from '../../ShedManagementTypes'
import { Box } from '@mui/material'
import { actions } from '../../ShedManagementState'
import * as S from './FormSearchLocation.styles'
import MultipleSelect from 'src/components/MultipleSelect/MultipleSelect'

type FilterField = 'streets' | 'shelves' | 'bins' | 'heightTypes' | 'isGondola' | 'isFresh'

const FormSearchLocation = () => {
  const {
    register, handleSubmit, watch, setValue, formState: { errors },
  } = useForm<GetLocationsPayload>()
  const dispatch = useDispatch()
  const fulfillmentCenters = useSelector(selectFulfillmentCenters)
  const {
    shedsSearchLocations, requestsSearchingFilters, filtersSearchLocations, locationHeightTypes,
  } = useSelector((state) => state.shedManagement)

  const fulfillmentCenterId = watch('fulfillmentCenterId')
  const sheds = watch('sheds')

  const onSubmit = (formData: GetLocationsPayload) => {
    dispatch(actions.getLocations(formData))
  }

  const fulfillmentCenterOptions = useMemo(() => fulfillmentCenters.map((fulfillmentCenter) => ({
    id: fulfillmentCenter.id,
    description: fulfillmentCenter.name,
  })), [fulfillmentCenters])

  const shedsOptions = useMemo(() => shedsSearchLocations.map((description, index) => ({
    id: index,
    description,
  })), [shedsSearchLocations])

  const isYesNoOptions = useMemo(() => isYesNoOptionsList.map((option) => ({
    id: option.id,
    description: option.description,
  })), [])

  const heightTypeOptions = useMemo(() => locationHeightTypes.map((locationHeightType) => ({
    id: locationHeightType.id,
    description: locationHeightType.name,
  })), [locationHeightTypes])

  const getFiltersSearchOptions = useCallback((param: FiltersSearchLocationsEnum) => (filtersSearchLocations[param]).map((description: string, index: number) => ({
    id: index,
    description,
  })), [filtersSearchLocations])

  useEffect(() => {
    dispatch(appActions.getAvailableFulfillmentCenters(defaultFulfillmentCenterTypeFilters))
  }, [dispatch])

  useEffect(() => {
    if (fulfillmentCenterId) {
      dispatch(actions.getShedsSearchLocations({ fulfillmentCenterId }))
    }
  }, [dispatch, fulfillmentCenterId])

  useEffect(() => {
    if (fulfillmentCenterId && sheds && sheds.length > 0) {
      dispatch(actions.getFiltersSearchLocations({
        fulfillmentCenterId,
        sheds,
      }))
    }
  }, [dispatch, sheds, fulfillmentCenterId])

  useEffect(() => {
    if (requestsSearchingFilters === 0 && sheds?.length === 0) {
      dispatch(actions.clearFiltersSearchLocations())
    }
  }, [dispatch, requestsSearchingFilters, sheds])

  useEffect(() => {
    const fieldsToReset = ['streets', 'shelves', 'bins', 'heightTypes', 'isGondola', 'isFresh']
    fieldsToReset.forEach((fieldName) => {
      setValue(fieldName as FilterField, [])
    })
  }, [setValue, sheds])

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        width: '100%',
        margin: '1.3rem 0 0 0',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '.6rem 2rem',
          width: '100%',
        }}
      >
        <S.CustomSelect
          name='fulfillmentCenterId'
          inputProps={{
            ...register(
              'fulfillmentCenterId',
              { required: 'Campo obrigatório' },
            ),
            'data-testid': 'fc-search-location',
          }}
          autoComplete='off'
          id='fulfillmentCenterId'
          label='Selecione um FC'
          variant='outlined'
          options={fulfillmentCenterOptions}
          error={!!errors.fulfillmentCenterId}
          helperText={errors.fulfillmentCenterId?.message}
          optionNone={false}
          type='text'
          fullWidth={false}
          autoFocus
          maxHeight='600px'
        />
        <MultipleSelect
          register={register}
          inputName='sheds'
          label='Galpão'
          errors={errors.sheds}
          options={shedsOptions}
          dataTestId='sheds-test-id'
          clearInputOnChange={fulfillmentCenterId}
          disabled={!shedsSearchLocations.length}
          required
          searchBar
          maxHeight='600px'
        />
        <MultipleSelect
          register={register}
          inputName='streets'
          label='Rua'
          errors={errors.streets}
          options={getFiltersSearchOptions(FiltersSearchLocationsEnum.Streets)}
          dataTestId='streets-test-id'
          clearInputOnChange={fulfillmentCenterId + sheds?.length}
          disabled={!filtersSearchLocations.streets.length}
          selectAll
          searchBar
          maxHeight='600px'
        />
        <MultipleSelect
          register={register}
          inputName='shelves'
          label='Estante'
          errors={errors.shelves}
          options={getFiltersSearchOptions(FiltersSearchLocationsEnum.Shelves)}
          dataTestId='shelves-test-id'
          clearInputOnChange={fulfillmentCenterId + sheds?.length}
          disabled={!filtersSearchLocations.shelves.length}
          selectAll
          searchBar
          maxHeight='600px'
        />
        <MultipleSelect
          register={register}
          inputName='bins'
          label='Escaninho'
          errors={errors.bins}
          options={getFiltersSearchOptions(FiltersSearchLocationsEnum.Bins)}
          dataTestId='bins-test-id'
          clearInputOnChange={fulfillmentCenterId + sheds?.length}
          disabled={!filtersSearchLocations.bins.length}
          selectAll
          searchBar
          maxHeight='600px'
        />
        <MultipleSelect
          register={register}
          inputName='heightTypes'
          label='Altura'
          errors={errors.heightTypes}
          options={heightTypeOptions}
          dataTestId='heightTypes-test-id'
          clearInputOnChange={fulfillmentCenterId + sheds?.length}
          disabled={!filtersSearchLocations.bins.length}
          selectAll
          optionProperty='id'
        />
        <MultipleSelect
          register={register}
          inputName='isGondola'
          label='É gôndola'
          errors={errors.isGondola}
          options={isYesNoOptions}
          dataTestId='isGondola-test-id'
          clearInputOnChange={fulfillmentCenterId + sheds?.length}
          disabled={!filtersSearchLocations.bins.length}
          selectAll
          optionProperty='id'
        />
        <MultipleSelect
          register={register}
          inputName='isFresh'
          label='É fresh'
          errors={errors.isFresh}
          options={isYesNoOptions}
          dataTestId='isFresh-test-id'
          clearInputOnChange={fulfillmentCenterId + sheds?.length}
          disabled={!filtersSearchLocations.bins.length}
          selectAll
          optionProperty='id'
        />
      </Box>
      <S.CustomButton
        title='Buscar'
        type='submit'
        bgColor='blue'
        hoverColor='darkBlueHover'
        letterColor='white'
        data-testid='search-location-button'
      />
    </Box>
  )
}

export default FormSearchLocation
